import React from 'react';
import PropTypes from 'prop-types';
import { WalteroTypography } from '../waltero';
import { formatDateTime, formatDate } from '../../utils/performanceFormatters';

// Shadow Update Overlay (Lines 1126-1230 from original)
const ShadowUpdateOverlay = ({ data, position }) => (
  <div 
    style={{
      position: 'fixed',
      left: position.x + 10,
      top: position.y - 10,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: '12px',
      borderRadius: '6px',
      fontSize: '12px',
      zIndex: 1000,
      pointerEvents: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      maxWidth: '400px',
      maxHeight: '200px',
      overflow: 'auto'
    }}
  >
    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
      Shadow Configuration Updates
    </div>
    <div style={{ marginBottom: '6px' }}>
      <strong>Device:</strong> {data.serialNumber}
    </div>
    <div style={{ marginBottom: '6px' }}>
      <strong>Total Updates:</strong> {data.count}
    </div>
    <div style={{ marginBottom: '8px' }}>
      <strong>Last Update:</strong> {formatDateTime(data.timestamp)}
    </div>
    
    {data.updates && data.updates.length > 0 && (
      <div>
        <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Recent Changes:</div>
        <div style={{ maxHeight: '100px', overflow: 'auto' }}>
          {data.updates.slice(0, 5).map((update, index) => (
            <div key={index} style={{ marginBottom: '4px', fontSize: '11px' }}>
              <div><strong>{update.field}:</strong> {update.oldValue} → {update.newValue}</div>
              <div style={{ color: '#ccc' }}>{formatDateTime(update.timestamp)}</div>
            </div>
          ))}
        </div>
      </div>
    )}
  </div>
);

// Boot Reason Overlay (Lines 1232-1330 from original)
const BootReasonOverlay = ({ data, position }) => (
  <div 
    style={{
      position: 'fixed',
      left: position.x + 10,
      top: position.y - 10,
      backgroundColor: 'rgba(0, 0, 0, 0.9)',
      color: 'white',
      padding: '12px',
      borderRadius: '6px',
      fontSize: '12px',
      zIndex: 1000,
      pointerEvents: 'none',
      boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
      minWidth: '250px'
    }}
  >
    <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
      Boot Reason Analysis
    </div>
    <div style={{ marginBottom: '6px' }}>
      <strong>Device:</strong> {data.serialNumber}
    </div>
    <div style={{ marginBottom: '6px' }}>
      <strong>Total Boots:</strong> {data.totalBoots}
    </div>
    <div style={{ marginBottom: '6px' }}>
      <strong>Problematic Boots:</strong> <span style={{ color: data.problematicBoots > 0 ? '#FF6B6B' : '#51CF66', fontWeight: 'bold' }}>{data.problematicBoots || 0}</span>
    </div>
    <div style={{ marginBottom: '8px' }}>
      <strong>OK Boots:</strong> <span style={{ color: '#51CF66', fontWeight: 'bold' }}>{data.okBoots || 0}</span>
    </div>
    
    {/* Show problematic boot reasons if any exist */}
    {data.problematicBootReasons && Object.keys(data.problematicBootReasons).length > 0 && (
      <div style={{ marginTop: '8px' }}>
        <div style={{ color: '#FF6B6B', fontWeight: 'bold', marginBottom: '4px' }}>⚠️ Problematic Boot Reasons:</div>
        {Object.entries(data.problematicBootReasons).map(([reason, count]) => (
          <div key={reason} style={{ marginLeft: '8px', fontSize: '11px', color: '#FF6B6B' }}>
            • {reason}: {count} occurrences
          </div>
        ))}
      </div>
    )}
    
    {/* Show OK boot reasons */}
    {data.okBootReasons && Object.keys(data.okBootReasons).length > 0 && (
      <div style={{ marginTop: '6px' }}>
        <div style={{ color: '#51CF66', fontWeight: 'bold', marginBottom: '4px' }}>✓ Normal Boot Reasons:</div>
        {Object.entries(data.okBootReasons).map(([reason, count]) => (
          <div key={reason} style={{ marginLeft: '8px', fontSize: '11px', color: '#51CF66' }}>
            • {reason}: {count} occurrences
          </div>
        ))}
      </div>
    )}
  </div>
);

// Payload Breakdown Overlay (Lines 1333-1420 from original)
const PayloadBreakdownOverlay = ({ data, position }) => {
  // Handle both formats - direct data or nested payloadData
  const payloadCount = data.totalPayloads || 
                       (data.payloadData && data.payloadData.transmissionCount) || 
                       data.transmissionWindows || 0;
  
  return (
    <div 
      style={{
        position: 'fixed',
        left: position.x + 10,
        top: position.y - 10,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '12px',
        zIndex: 1000,
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        minWidth: '250px',
        maxWidth: '350px'
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
        Data Payload Details
      </div>
      <div style={{ marginBottom: '6px' }}>
        <strong>Device:</strong> {data.serialNumber}
      </div>
      {data.meterName && (
        <div style={{ marginBottom: '6px' }}>
          <strong>Meter Name:</strong> {data.meterName}
        </div>
      )}
      <div style={{ marginBottom: '6px' }}>
        <strong>Received Date:</strong> {formatDate(data.date)}
      </div>
      <div style={{ marginBottom: '6px' }}>
        <strong>Total Payloads Received:</strong> <span style={{ 
          color: payloadCount > 0 ? '#51CF66' : '#FF6B6B',
          fontWeight: 'bold' 
        }}>{payloadCount}</span>
      </div>
      
      {data.measurementBreakdown && Object.keys(data.measurementBreakdown).length > 0 && (
        <div style={{ marginTop: '8px' }}>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Measurement Date Breakdown:</div>
          <div style={{ maxHeight: '150px', overflow: 'auto' }}>
            {Object.entries(data.measurementBreakdown)
              .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
              .map(([measurementDate, count]) => (
                <div key={measurementDate} style={{ 
                  marginLeft: '8px', 
                  fontSize: '11px',
                  marginBottom: '3px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <span style={{ color: '#ccc' }}>
                    {formatDate(measurementDate)}:
                  </span>
                  <span style={{ 
                    fontWeight: 'bold',
                    color: '#51CF66',
                    marginLeft: '10px'
                  }}>
                    {count} payloads
                  </span>
                </div>
              ))}
          </div>
        </div>
      )}
      
      {data.transmissionWindows && (
        <div style={{ marginTop: '6px', fontSize: '11px', color: '#FFD93D' }}>
          <strong>Transmission Windows:</strong> {data.transmissionWindows}
        </div>
      )}
    </div>
  );
};

// Digit Position Overlay (Lines 1422-1470 from original)
const DigitPositionOverlay = ({ data, position }) => {
  let processedPositions = [];
  let totalReadings = 0;
  let binarySuccessPercentage = null;

  // Handle different data formats: table hover vs bar chart hover
  if (data.positions && Array.isArray(data.positions)) {
    // Full digitPositionStats from table hover - process all positions
    processedPositions = data.positions.map((pos, index) => {
      const totalAttempts = pos.totalAttempts || 0;
      const failures = pos.failures || 0;
      const lowConfidence = pos.lowConfidence || 0;
      const successes = totalAttempts - failures - lowConfidence;
      const successRate = totalAttempts > 0 ? (successes / totalAttempts) * 100 : 0;
      
      return {
        position: index + 1, // Position number (1-based)
        success: successes,
        attempts: totalAttempts,
        failures: failures,
        lowConfidence: lowConfidence,
        successRate: successRate
      };
    });
    totalReadings = data.totalReadings || 0;
    binarySuccessPercentage = data.binarySuccessPercentage;
  } else if (data.position && (data.type === 'failure' || data.type === 'lowConfidence')) {
    // Single position data from bar chart hover
    const positionNumber = data.position;
    const totalAttempts = data.total || 0;
    const count = data.count || 0;
    const percentage = data.percentage || 0;
    
    let failures = 0;
    let lowConfidence = 0;
    
    if (data.type === 'failure') {
      failures = count;
    } else if (data.type === 'lowConfidence') {
      lowConfidence = count;
    }
    
    const successes = totalAttempts - failures - lowConfidence;
    const successRate = totalAttempts > 0 ? (successes / totalAttempts) * 100 : 0;
    
    processedPositions = [{
      position: positionNumber,
      success: successes,
      attempts: totalAttempts,
      failures: failures,
      lowConfidence: lowConfidence,
      successRate: successRate,
      highlightType: data.type, // Mark which metric is being highlighted
      highlightPercentage: percentage
    }];
    totalReadings = totalAttempts;
  }

  // Calculate dynamic positioning to keep overlay within viewport
  const overlayWidth = 320;
  const overlayHeight = Math.min(400, 100 + (processedPositions.length * 35)); // Estimate height
  
  let left = position.x + 10;
  let top = position.y - 10;
  
  // Adjust if overlay would go off screen
  if (left + overlayWidth > window.innerWidth) {
    left = position.x - overlayWidth - 10;
  }
  if (top + overlayHeight > window.innerHeight) {
    top = position.y - overlayHeight - 10;
  }
  if (top < 0) {
    top = 10;
  }

  return (
    <div 
      style={{
        position: 'fixed',
        left: left,
        top: top,
        backgroundColor: 'rgba(0, 0, 0, 0.9)',
        color: 'white',
        padding: '12px',
        borderRadius: '6px',
        fontSize: '12px',
        zIndex: 1000,
        pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        width: `${overlayWidth}px`,
        maxHeight: '90vh' // Ensure it never exceeds viewport height
      }}
    >
      <div style={{ fontWeight: 'bold', marginBottom: '8px', fontSize: '13px' }}>
        {processedPositions.length === 1 && processedPositions[0].highlightType ? 
          `Position ${processedPositions[0].position} - ${processedPositions[0].highlightType === 'failure' ? 'Failures' : 'Low Confidence'} Analysis` :
          'Digit Position Analysis'
        }
      </div>
      <div style={{ marginBottom: '6px' }}>
        <strong>Total Positions Analyzed:</strong> {processedPositions.length}
      </div>
      <div style={{ marginBottom: '6px' }}>
        <strong>Total Readings:</strong> {totalReadings}
      </div>
      
      {processedPositions.length > 0 && (
        <div>
          <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>Position Statistics:</div>
          {processedPositions.map((pos) => (
            <div key={pos.position} style={{ marginBottom: '3px', fontSize: '11px' }}>
              <span style={{ color: pos.successRate >= 80 ? '#51CF66' : pos.successRate >= 60 ? '#FFD93D' : '#FF6B6B' }}>
                Pos {pos.position}: {pos.success}/{pos.attempts} ({pos.successRate.toFixed(1)}%)
              </span>
              {/* Show highlighted metric for bar chart hovers */}
              {pos.highlightType && (
                <div style={{ marginLeft: '12px', fontSize: '10px', color: '#FFD93D', fontWeight: 'bold' }}>
                  {pos.highlightType === 'failure' ? '🔴 Failures' : '⚠️ Low Confidence'}: {pos.highlightPercentage.toFixed(2)}%
                </div>
              )}
              {/* Show breakdown for all hover types */}
              {(pos.failures > 0 || pos.lowConfidence > 0) && (
                <div style={{ marginLeft: '12px', fontSize: '10px', color: '#ccc' }}>
                  {pos.failures > 0 && `${pos.failures} failed`}
                  {pos.failures > 0 && pos.lowConfidence > 0 && ', '}
                  {pos.lowConfidence > 0 && `${pos.lowConfidence} low confidence`}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {binarySuccessPercentage !== undefined && binarySuccessPercentage !== null && (
        <div style={{ marginTop: '8px', fontSize: '11px', color: '#FFD93D' }}>
          <strong>Binary Success Rate:</strong> {binarySuccessPercentage.toFixed(1)}%
        </div>
      )}
    </div>
  );
};

// Main overlay component that handles all 4 types
const PerformanceDataOverlays = ({ 
  hoverData, 
  hoverPosition,
  shadowUpdateHoverData,
  shadowUpdateHoverPosition, 
  bootReasonHoverData,
  bootReasonHoverPosition,
  payloadHoverData,
  payloadHoverPosition
}) => {
  return (
    <>
      {/* Digit Position Stats Overlay */}
      {hoverData && (
        <DigitPositionOverlay 
          data={hoverData} 
          position={hoverPosition} 
        />
      )}
      
      {/* Shadow Update Overlay */}
      {shadowUpdateHoverData && (
        <ShadowUpdateOverlay 
          data={shadowUpdateHoverData} 
          position={shadowUpdateHoverPosition} 
        />
      )}
      
      {/* Boot Reason Overlay */}
      {bootReasonHoverData && (
        <BootReasonOverlay 
          data={bootReasonHoverData} 
          position={bootReasonHoverPosition} 
        />
      )}
      
      {/* Payload Breakdown Overlay */}
      {payloadHoverData && (
        <PayloadBreakdownOverlay 
          data={payloadHoverData} 
          position={payloadHoverPosition} 
        />
      )}
    </>
  );
};

PerformanceDataOverlays.propTypes = {
  hoverData: PropTypes.object,
  hoverPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  shadowUpdateHoverData: PropTypes.object,
  shadowUpdateHoverPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  bootReasonHoverData: PropTypes.object,
  bootReasonHoverPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  }),
  payloadHoverData: PropTypes.object,
  payloadHoverPosition: PropTypes.shape({
    x: PropTypes.number,
    y: PropTypes.number
  })
};

export default PerformanceDataOverlays;